/**
 * Message field for a Fellows Corner mentorship inquiry.
 */

import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';

import { Button, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { FELLOWS_QUESTION_PLACEHOLDER } from '@/constants/fellowsCorner';
import type { MentorshipFormData } from '@/hooks/useMentorshipInquiryForm';

type MentorshipQuestionFieldProps = {
  control: Control<MentorshipFormData>;
  errors: FieldErrors<MentorshipFormData>;
  isDisabled?: boolean;
  placeholder?: string;
  /** When provided, renders a "View guidelines" link next to the label. */
  onOpenGuidelines?: () => void;
};

export function MentorshipQuestionField({
  control,
  errors,
  isDisabled = false,
  placeholder = FELLOWS_QUESTION_PLACEHOLDER,
  onOpenGuidelines,
}: MentorshipQuestionFieldProps) {
  return (
    <Controller
      name="message"
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState, formState }) => {
        const showError =
          !!errors.message?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <FormControlLabel className="mb-2">
              <HStack className="items-center justify-between gap-1.5">
                <FormControlLabelText
                  size="sm"
                  className="font-semibold leading-normal text-typography-950">
                  Question*
                </FormControlLabelText>
                {onOpenGuidelines ? (
                  <Button onPress={onOpenGuidelines} variant="link" size="sm" action="secondary">
                    <ButtonText className="text-md text-info-700 underline data-[hover=true]:text-info-800">
                      View Guidelines
                    </ButtonText>
                  </Button>
                ) : null}
              </HStack>
            </FormControlLabel>

            <Textarea className="min-h-56">
              <TextareaInput
                testID="mentorship-message-field"
                placeholder={placeholder}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                multiline
                textAlignVertical="top"
              />
            </Textarea>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.message?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
